import React from "react";
import { CreditCard, Clock, Shield, ArrowRightLeft, Phone, Mail } from "lucide-react";

const Refund = () => {
  const policies = [
    {
      icon: CreditCard,
      title: "No Training Charges",
      desc: "Student Era does not charge any tuition or fees for course content, learning materials, mentor reviews, or digital certificate generation. Access to educational resources is completely free."
    },
    {
      icon: Clock,
      title: "Administrative Processing Fee",
      desc: "To offset costs related to third-party verification APIs, cloud hosting logs, and to ensure genuine applications, a nominal administration fee of ₹149 is charged during registration."
    }
  ];

  const eligibilityItems = [
    { title: "Technical Checkout Errors", desc: "Transactions that fail to link to a student profile due to payment gateway API timeouts." },
    { title: "Duplicate Invoice Charges", desc: "Accidental double-billing transactions processed on the same student profile." },
    { title: "Domain Class Cancellation", desc: "If Student Era is forced to cancel an internship cohort prior to its kickoff date." }
  ];

  const steps = [
    { num: "01", label: "Email Support", desc: "Message us at contact@studentera.online within 7 days of the payment." },
    { num: "02", label: "Provide Details", desc: "Include your transaction reference ID and student registration email." },
    { num: "03", label: "Finance Review", desc: "Our audit team verifies transaction logs with the gateway provider." },
    { num: "04", label: "Direct Credit", desc: "Approved refunds are credited to the source account in 5-7 business days." }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 font-sans">
      {/* Background Decorative Blobs */}
      <div className="absolute top-1/4 -left-32 w-72 sm:w-96 h-72 sm:h-96 bg-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-32 w-72 sm:w-96 h-72 sm:h-96 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse delay-1000 pointer-events-none"></div>

      <div className="relative max-w-5xl mx-auto z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-center mb-4 text-gradient-premium">
            Refund Policy
          </h1>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto font-medium">
            Clear and transparent details about our administrative fee structures and refund guidelines.
          </p>
        </div>

        {/* Top Split Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {policies.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div
                key={idx}
                className="glass-card rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 flex gap-5 hover:border-indigo-300/40 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex-shrink-0 bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-3 rounded-2xl h-fit shadow-sm">
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2 tracking-tight">{p.title}</h3>
                  <p className="text-slate-650 text-sm sm:text-base leading-relaxed font-medium">
                    {p.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
          {/* Eligibility Section (left) */}
          <div className="lg:col-span-7 glass-card rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 flex flex-col justify-between hover:border-indigo-300/40 transition-all duration-300">
            <div>
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-shrink-0 bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-2.5 rounded-xl">
                  <Shield className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">Refund Eligibility</h2>
              </div>
              <p className="text-slate-500 text-sm mb-5 font-medium">
                Refunds for the administrative fee are analyzed on a case-by-case basis under the following categories:
              </p>
              <div className="space-y-4">
                {eligibilityItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3.5 bg-white/60 hover:bg-white border border-slate-100 rounded-2xl p-4 transition-all duration-200 shadow-sm"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0"></div>
                    <div>
                      <strong className="text-slate-800 font-extrabold text-sm block mb-0.5">{item.title}</strong>
                      <p className="text-slate-650 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Refund Steps (right) */}
          <div className="lg:col-span-5 glass-card rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 flex flex-col hover:border-indigo-300/40 transition-all duration-300">
            <div className="flex items-center gap-4 mb-5">
              <div className="flex-shrink-0 bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-2.5 rounded-xl">
                <ArrowRightLeft className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 tracking-tight">Process Workflow</h2>
            </div>
            <p className="text-slate-500 text-sm mb-5 font-medium">
              Submit your refund request by following these steps:
            </p>
            <div className="space-y-4 flex-grow flex flex-col justify-between">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-4 items-center">
                  <span className="text-2xl font-black text-indigo-200 tracking-tight shrink-0 select-none">
                    {step.num}
                  </span>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800">{step.label}</h4>
                    <p className="text-xs text-slate-555 leading-relaxed mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section (Full Width) */}
        <div className="glass-card rounded-3xl p-6 sm:p-10 shadow-xl shadow-slate-100/50 border border-white/60 text-center flex flex-col items-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-3 tracking-tight">Need Support With Payments?</h2>
          <p className="text-slate-500 text-sm sm:text-base max-w-xl mb-6 font-medium leading-relaxed">
            If you encounter payment issues or require clarification on refunds, contact our financial support center.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-md">
            {/* Email link */}
            <a
              href="mailto:contact@studentera.online"
              className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-sm hover:shadow active:scale-95 transition-all duration-200 w-full sm:w-auto text-sm sm:text-base"
            >
              <Mail className="h-5 w-5 text-indigo-200" />
              <span>contact@studentera.online</span>
            </a>

            {/* Telephone link */}
            <a
              href="tel:+15551234567"
              className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-2xl shadow-sm hover:shadow active:scale-95 transition-all duration-200 w-full sm:w-auto text-sm sm:text-base"
            >
              <Phone className="h-5 w-5 text-slate-400" />
              <span>+1 (555) 123-4567</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
